import { ConditionNode, QueryNode, LogicalNode } from "modules/record/record-producer/types/query.types";

export function updateNode<T extends QueryNode>(
    root: T,
    updated: QueryNode
): T {
    if (root.id === updated.id) {
        return updated as T;
    }

    if (root.type === "logical") {
        return {
            ...root,
            conditions: root.conditions.map((child) =>
                updateNode(child, updated)
            ),
        } as T;
    }

    return root;
}

export function createEmptyCondition(): QueryNode {
    return {
        id: crypto.randomUUID(),
        type: "condition",
        field: "",
        operator: "",
        value: undefined,
    };
}

export function createExistsNode(): QueryNode {
    return {
        id: crypto.randomUUID(),
        type: "exists",
        table: "",
        conditions: [
            {
                id: crypto.randomUUID(),
                type: "condition",
                field: "",
                operator: "",
                value: undefined,
            }
        ],
    };
}

export function createLogicalGroup(): LogicalNode {
    return {
        id: crypto.randomUUID(),
        type: "logical",
        operator: "and",
        conditions: [
            createEmptyCondition()
        ],
    };
}

export function normalizeLogicalNode(input: any): LogicalNode {
    if (input && typeof input === "string") {
        input = JSON.parse(input);
    }

    if (input == null || input === "") {
        return createLogicalGroup();
    }

    if (input.type === "condition" || input.type === "exists") {
        const child = normalizeQueryNode(input);
        if (!child) throw new Error("Invalid condition node");
        return { id: crypto.randomUUID(), type: "logical", operator: "and", conditions: [child] };
    }
    if (input.type !== "logical" || !Array.isArray(input.conditions) || !["and", "or"].includes(input.operator)) {
        throw new Error("Invalid condition root");
    }

    return {
        id: input.id ?? crypto.randomUUID(),
        type: "logical",
        operator: input.operator === "or" ? "or" : "and",
        conditions: Array.isArray(input.conditions)
            ? input.conditions.map((node: any) => {
                const result = normalizeQueryNode(node);
                if (!result) throw new Error("Invalid condition child");
                return result;
            })
            : [],
    };
}

function normalizeQueryNode(node: any): QueryNode | null {
    if (!node || typeof node !== "object") return null;

    if (node.type === "condition") {
        if (typeof node.field !== "string" || typeof node.operator !== "string") return null;
        return {
            id: node.id ?? crypto.randomUUID(),
            type: "condition",
            field: node.field ?? "",
            operator: node.operator ?? "",
            value: node.value,
        };
    }

    if (node.type === "logical") {
        return normalizeLogicalNode(node);
    }

    if (node.type === "exists") {
        if (typeof node.table !== "string" || !Array.isArray(node.conditions)) return null;
        return {
            id: node.id ?? crypto.randomUUID(),
            type: "exists",
            table: node.table ?? "",
            conditions: node.conditions.map((condition: any) => {
                const result = normalizeQueryNode(condition);
                if (result?.type !== "condition") throw new Error("Invalid EXISTS condition");
                return result;
            }),
        };
    }

    return null;
}

export function removeNodeById(
    node: QueryNode,
    id: string
): QueryNode | null {
    if (node.type === "condition" || node.type === "exists") {
        return node.id === id ? null : node;
    }

    const children = node.conditions
        .map((child) => removeNodeById(child, id))
        .filter(Boolean) as QueryNode[];

    if (children.length === 0) return null;

    return {
        ...node,
        conditions: children,
    };
}

type QueryNodeFactory = () => QueryNode;

function addNodeAfter(
    parentNode: LogicalNode,
    nodeId: string,
    newOperator: 'and' | 'or',
    createNode: QueryNodeFactory
): LogicalNode {
    if (parentNode.conditions.length === 1 && parentNode.operator !== newOperator) {
        const nodeIndex = parentNode.conditions.findIndex(
            (candidate) => candidate.id === nodeId
        );

        if (nodeIndex !== -1) {
            return {
                ...parentNode,
                operator: newOperator,
                conditions: [...parentNode.conditions, createNode()],
            };
        }
    }

    if (parentNode.operator === newOperator) {
        const nodeIndex = parentNode.conditions.findIndex(
            (candidate) => candidate.id === nodeId
        );

        if (nodeIndex !== -1) {
            const newConditions = [...parentNode.conditions];
            newConditions.splice(nodeIndex + 1, 0, createNode());

            return {
                ...parentNode,
                conditions: newConditions,
            };
        }
    }

    const replaceTarget = (node: QueryNode): QueryNode => {
        if ((node.type === "condition" || node.type === "exists") && node.id === nodeId) {
            return {
                id: crypto.randomUUID(),
                type: "logical",
                operator: newOperator,
                conditions: [node, createNode()],
            } as LogicalNode;
        }

        if (node.type === "logical") {
            return addNodeAfter(node, nodeId, newOperator, createNode);
        }

        return node;
    };

    return {
        ...parentNode,
        conditions: parentNode.conditions.map(replaceTarget),
    };
}

export function addExistsAfter(
    parentNode: LogicalNode,
    nodeId: string,
    operator: 'and' | 'or' = 'and'
): LogicalNode {
    return addNodeAfter(parentNode, nodeId, operator, createExistsNode);
}

export function addConditionOrGroup(
    parentNode: LogicalNode,
    conditionId: string,
    newOperator: 'and' | 'or'
): LogicalNode {
    return addNodeAfter(parentNode, conditionId, newOperator, createEmptyCondition);
}
